import { useEffect, useRef } from 'react'
import { botApi } from '../api'
import { parseProgressTimestamp } from '../utils/timeParsing'

const BACKTEST_STATUS_PENDING = 0
const BACKTEST_STATUS_RUNNING = 1

interface UseBacktestProgressOptions {
  btid: string
  /**
   * Current backtest status, owned by the parent. The hook reads this to
   * decide whether to keep polling and to seed initial progress; it never
   * stores its own copy.
   */
  backtestStatus: number
  initialProgressBacktest: string | null
  rangeStartTs: number
  rangeEndTs: number
  updateBacktestProgress: (maxDateTime: number) => void
  /**
   * Called only when the polled status changes. The parent should hold
   * the canonical backtestStatus state.
   */
  onStatusChange: (status: number) => void
  pollIntervalMs?: number
}

export default function useBacktestProgress({
  btid,
  backtestStatus,
  initialProgressBacktest,
  rangeStartTs,
  rangeEndTs,
  updateBacktestProgress,
  onStatusChange,
  pollIntervalMs = 3000,
}: UseBacktestProgressOptions): void {
  const pollingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isPollingRequestPendingRef = useRef(false)
  const lastProgressTsRef = useRef<number | null>(null)

  // Seed updateBacktestProgress whenever backtestStatus transitions. The
  // parent owns the status, so we just react to it.
  useEffect(() => {
    if (backtestStatus === BACKTEST_STATUS_PENDING) {
      updateBacktestProgress(rangeStartTs)
      lastProgressTsRef.current = rangeStartTs
      return
    }

    if (backtestStatus === BACKTEST_STATUS_RUNNING) {
      const initialTs = parseProgressTimestamp(initialProgressBacktest) ?? rangeStartTs
      updateBacktestProgress(initialTs)
      lastProgressTsRef.current = initialTs
      return
    }

    updateBacktestProgress(rangeEndTs)
    lastProgressTsRef.current = rangeEndTs
  }, [backtestStatus, initialProgressBacktest, updateBacktestProgress, rangeStartTs, rangeEndTs])

  useEffect(() => {
    if (backtestStatus !== BACKTEST_STATUS_RUNNING && backtestStatus !== BACKTEST_STATUS_PENDING) return

    let cancelled = false

    const scheduleNext = () => {
      if (cancelled) return
      pollingTimerRef.current = setTimeout(runPoll, pollIntervalMs)
    }

    const runPoll = async () => {
      if (cancelled || isPollingRequestPendingRef.current) {
        scheduleNext()
        return
      }

      isPollingRequestPendingRef.current = true
      try {
        const progress = await botApi.getBacktestProgress(btid)
        if (cancelled) return

        if (progress.status !== backtestStatus) {
          onStatusChange(progress.status)
        }

        if (progress.status === BACKTEST_STATUS_PENDING) {
          scheduleNext()
          return
        }

        if (progress.status !== BACKTEST_STATUS_RUNNING) {
          updateBacktestProgress(rangeEndTs)
          lastProgressTsRef.current = rangeEndTs
          return
        }

        const nextTs = parseProgressTimestamp(progress.progressBacktest)
        if (nextTs !== null && nextTs !== lastProgressTsRef.current) {
          updateBacktestProgress(nextTs)
          lastProgressTsRef.current = nextTs
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Backtest progress polling failed:', err)
        }
      } finally {
        isPollingRequestPendingRef.current = false
      }

      scheduleNext()
    }

    runPoll()

    return () => {
      cancelled = true
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current)
        pollingTimerRef.current = null
      }
    }
  }, [btid, backtestStatus, updateBacktestProgress, rangeEndTs, pollIntervalMs, onStatusChange])
}
